import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';

const AuthContext = createContext({
  loading: true,
  session: null,
  profile: null,
  aal: null,             // { currentLevel, nextLevel } from Supabase MFA
  mfaRequired: false,    // signed in at aal1 but a verified factor exists → must challenge
  signInWithPassword: async () => {},
  signInWithMagicLink: async () => {},
  signUp: async () => {},
  signOut: async () => {},
  refreshProfile: async () => {},
  refreshAAL: async () => {},
  listFactors: async () => ({ totp: [] }),
  enrollTotp: async () => {},
  verifyEnrollment: async () => {},
  challengeAndVerify: async () => {},
  unenrollFactor: async () => {},
});

// Boot timeout: if we can't resolve auth state in this many ms (network blip,
// Supabase brownout, hung getSession() promise), we force-flip to a signed-out
// state so ProtectedRoute can redirect to /signin instead of spinning forever.
const BOOT_TIMEOUT_MS = 8000;

// Detect 401-equivalent errors from Supabase PostgREST. The JS client surfaces
// JWT expiry as code `PGRST301`; we also pattern-match the message in case the
// error code is missing on some auth-touching paths.
const isAuthError = (err) => {
  if (!err) return false;
  if (err.code === 'PGRST301') return true;
  if (err.status === 401) return true;
  const msg = (err.message || '').toLowerCase();
  return msg.includes('jwt expired') || msg.includes('invalid jwt') || msg.includes('jwt issued');
};

const fetchProfile = async (userId) => {
  if (!supabase) return { ok: false, data: null, authError: false };
  const { data, error } = await supabase
    .from('profiles')
    .select('id, email, full_name, status, role, created_at')
    .eq('id', userId)
    .maybeSingle();
  if (error) {
    // eslint-disable-next-line no-console
    console.error('[auth] fetchProfile failed', error);
    return { ok: false, data: null, authError: isAuthError(error) };
  }
  return { ok: true, data, authError: false };
};

export const AuthProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [aal, setAal] = useState(null); // { currentLevel, nextLevel }
  const bootTimerRef = useRef(null);

  // Assurance level: 'aal1' = password only, 'aal2' = password + TOTP verified.
  // If a user has a verified factor, Supabase reports nextLevel='aal2'; until
  // they complete the TOTP challenge, currentLevel stays 'aal1'. That gap is
  // our "must challenge" signal.
  const refreshAAL = useCallback(async () => {
    if (!supabase) { setAal(null); return null; }
    try {
      const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (error || !data) { setAal(null); return null; }
      setAal({ currentLevel: data.currentLevel, nextLevel: data.nextLevel });
      return data;
    } catch {
      setAal(null);
      return null;
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!supabase || !session?.user?.id) {
      setProfile(null);
      return;
    }
    const result = await fetchProfile(session.user.id);
    if (result.authError) {
      // Profile fetch failed with an auth error → session is dead. Clear it
      // so ProtectedRoute bounces to /signin instead of rendering the loader.
      setProfile(null);
      setSession(null);
      return;
    }
    setProfile(result.data);
  }, [session]);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      // Mock mode for local dev — auto-approve so the dashboard renders.
      setSession({ user: { id: 'mock-user', email: 'demo@coynitt.ca' } });
      setProfile({
        id: 'mock-user',
        email: 'demo@coynitt.ca',
        full_name: 'Demo User',
        status: 'approved',
        role: 'admin',
      });
      setLoading(false);
      return;
    }

    let active = true;

    // Hard floor: if we're still in `loading=true` after BOOT_TIMEOUT_MS,
    // treat the auth bootstrap as failed and surface a signed-out state.
    // ProtectedRoute will then redirect to /signin (cleanly recoverable),
    // rather than hanging on the spinner forever.
    bootTimerRef.current = setTimeout(() => {
      if (!active) return;
      // eslint-disable-next-line no-console
      console.warn('[auth] boot timeout — forcing signed-out state');
      setSession(null);
      setProfile(null);
      setLoading(false);
    }, BOOT_TIMEOUT_MS);

    const finishBoot = () => {
      if (bootTimerRef.current) {
        clearTimeout(bootTimerRef.current);
        bootTimerRef.current = null;
      }
      if (active) setLoading(false);
    };

    supabase.auth.getSession()
      .then(async ({ data }) => {
        if (!active) return;
        const s = data?.session ?? null;
        setSession(s);
        if (s?.user?.id) {
          const result = await fetchProfile(s.user.id);
          if (!active) return;
          if (result.authError) {
            // Stored token is stale and Supabase couldn't refresh it.
            // Drop session so ProtectedRoute redirects to /signin.
            setSession(null);
            setProfile(null);
          } else {
            setProfile(result.data);
          }
        }
        finishBoot();
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[auth] getSession() rejected', err);
        if (!active) return;
        setSession(null);
        setProfile(null);
        finishBoot();
      });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      // Differentiate so we always reach a terminal state:
      //   SIGNED_OUT       → clear everything, let ProtectedRoute redirect
      //   TOKEN_REFRESHED  → re-fetch profile against the new token
      //   SIGNED_IN / INITIAL_SESSION / others → mirror session, fetch profile
      if (event === 'SIGNED_OUT') {
        setSession(null);
        setProfile(null);
        return;
      }
      setSession(newSession ?? null);
      if (newSession?.user?.id) {
        const result = await fetchProfile(newSession.user.id);
        if (result.authError) {
          setSession(null);
          setProfile(null);
        } else {
          setProfile(result.data);
        }
      } else {
        setProfile(null);
      }
    });

    return () => {
      active = false;
      if (bootTimerRef.current) {
        clearTimeout(bootTimerRef.current);
        bootTimerRef.current = null;
      }
      subscription?.unsubscribe();
    };
  }, []);

  const signInWithPassword = useCallback(async (email, password) => {
    if (!supabase) throw new Error('Supabase is not configured.');
    if (!email.toLowerCase().endsWith('@coynitt.ca')) {
      throw new Error('Only @coynitt.ca email addresses can sign in.');
    }
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  }, []);

  const signInWithMagicLink = useCallback(async (email) => {
    if (!supabase) throw new Error('Supabase is not configured.');
    if (!email.toLowerCase().endsWith('@coynitt.ca')) {
      throw new Error('Only @coynitt.ca email addresses can sign in.');
    }
    const redirectTo = `${window.location.origin}/`;
    const { error } = await supabase.auth.signInWithOtp({ email, options: { emailRedirectTo: redirectTo } });
    if (error) throw error;
  }, []);

  const signUp = useCallback(async (email, password) => {
    if (!supabase) throw new Error('Supabase is not configured.');
    if (!email.toLowerCase().endsWith('@coynitt.ca')) {
      throw new Error('Only @coynitt.ca email addresses can sign up.');
    }
    const { error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;
  }, []);

  const signOut = useCallback(async () => {
    if (!supabase) return;
    await supabase.auth.signOut();
  }, []);

  // ── MFA (TOTP) helpers ───────────────────────────────────────
  const listFactors = useCallback(async () => {
    if (!supabase) return { totp: [] };
    const { data, error } = await supabase.auth.mfa.listFactors();
    if (error) throw error;
    return data;
  }, []);

  // Begin enrollment: returns { id, totp: { qr_code, secret, uri } } so the UI
  // can render the QR + manual-entry secret for the authenticator app.
  const enrollTotp = useCallback(async (friendlyName = 'Authenticator') => {
    if (!supabase) throw new Error('Supabase is not configured.');
    const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp', friendlyName });
    if (error) throw error;
    return data;
  }, []);

  // Confirm enrollment by verifying the first code from the app. On success the
  // factor becomes 'verified' and the session is elevated to aal2.
  const verifyEnrollment = useCallback(async (factorId, code) => {
    if (!supabase) throw new Error('Supabase is not configured.');
    const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId, code });
    if (error) throw error;
    await refreshAAL();
  }, [refreshAAL]);

  // Login-time challenge: elevate an aal1 session to aal2 with a fresh code.
  const challengeAndVerify = useCallback(async (code) => {
    if (!supabase) throw new Error('Supabase is not configured.');
    const { data: factors, error: fErr } = await supabase.auth.mfa.listFactors();
    if (fErr) throw fErr;
    const totp = (factors?.totp || []).find((f) => f.status === 'verified');
    if (!totp) throw new Error('No verified authenticator is set up on this account.');
    const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId: totp.id, code });
    if (error) throw error;
    await refreshAAL();
  }, [refreshAAL]);

  const unenrollFactor = useCallback(async (factorId) => {
    if (!supabase) throw new Error('Supabase is not configured.');
    const { error } = await supabase.auth.mfa.unenroll({ factorId });
    if (error) throw error;
    await refreshAAL();
  }, [refreshAAL]);

  // Keep AAL in sync with the session — every sign-in / refresh / sign-out.
  useEffect(() => {
    if (isSupabaseConfigured && session?.user?.id) {
      refreshAAL();
    } else {
      setAal(null);
    }
  }, [session, refreshAAL]);

  const mfaRequired = aal?.currentLevel === 'aal1' && aal?.nextLevel === 'aal2';

  return (
    <AuthContext.Provider
      value={{
        loading, session, profile, aal, mfaRequired,
        signInWithPassword, signInWithMagicLink, signUp, signOut, refreshProfile,
        refreshAAL, listFactors, enrollTotp, verifyEnrollment, challengeAndVerify, unenrollFactor,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
